import { Link } from '@tanstack/react-router'
import { X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Alert, buttonVariants } from '@/components/ui'

import { decideBanner } from '../bannerDecision'
import { formatCount, formatShortDate } from '../format'
import { useUsageSummary } from '../queries'

const TRIAL_DISMISS_KEY = 'codout.billing.trialBannerDismissed'

function readDismissed(tenantId: string): boolean {
  try {
    return window.sessionStorage.getItem(`${TRIAL_DISMISS_KEY}:${tenantId}`) === '1'
  } catch {
    return false
  }
}

function writeDismissed(tenantId: string): void {
  try {
    window.sessionStorage.setItem(`${TRIAL_DISMISS_KEY}:${tenantId}`, '1')
  } catch {
    // Armazenamento indisponível (aba privada): o aviso só volta a aparecer, sem quebrar.
  }
}

export function BillingBanner({
  tenantId,
  tenantSlug,
}: {
  readonly tenantId: string
  readonly tenantSlug: string
}) {
  const { t, i18n } = useTranslation()
  const summary = useUsageSummary(tenantId)
  const [dismissed, setDismissed] = useState(() => readDismissed(tenantId))

  if (summary.data === undefined) {
    return null
  }

  const decision = decideBanner(summary.data)
  if (decision === null || (decision.kind === 'trial' && dismissed)) {
    return null
  }

  const data = summary.data
  const available = formatCount(Math.max(0, data.available), i18n.language)
  const title =
    decision.kind === 'lowBalance'
      ? t('billing.banner.lowBalance', { available })
      : decision.kind === 'trial'
        ? t('billing.banner.trial', {
            available,
            date: data.trialEndsAt === null ? '' : formatShortDate(data.trialEndsAt, i18n.language),
          })
        : t(`billing.banner.${decision.kind}`)

  const paymentUrl = data.openInvoice?.paymentUrl ?? null
  const payable =
    (decision.kind === 'pastDue' || decision.kind === 'openInvoice') && paymentUrl !== null

  return (
    <div className="border-b border-border-default px-4 py-2" data-testid="billing-banner">
      <Alert variant={decision.variant} title={title}>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          {payable ? (
            <a
              href={paymentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={buttonVariants({ variant: 'primary', size: 'sm' })}
            >
              {t('billing.banner.actions.payNow')}
            </a>
          ) : (
            <Link
              to="/app/$tenantSlug/billing"
              params={{ tenantSlug }}
              className={buttonVariants({ variant: 'secondary', size: 'sm' })}
            >
              {decision.kind === 'lowBalance'
                ? t('billing.banner.actions.viewUsage')
                : t('billing.banner.actions.viewPlans')}
            </Link>
          )}
          {decision.kind === 'trial' ? (
            <button
              type="button"
              onClick={() => {
                writeDismissed(tenantId)
                setDismissed(true)
              }}
              aria-label={t('billing.banner.actions.dismiss')}
              className="ml-auto flex h-7 w-7 items-center justify-center rounded-md text-foreground-muted hover:bg-background-elevated focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
            >
              <X aria-hidden="true" className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      </Alert>
    </div>
  )
}
